import { IconType } from 'react-icons'
import {
  LuCircleHelp,
  LuClock,
  LuOctagonMinus,
  LuPause,
  LuTrophy,
  LuUsers,
} from 'react-icons/lu'

import { REFERRAL_STAGE_LABEL } from '@/lib/constants'
import { ChartColorGroupName } from '@/lib/services/chartColoursService'
import { ReferralStatusEntity } from '@/types/entities'
import { ReferralStage } from '@/types/misc'

export interface ReferralStageInfo {
  code: ReferralStage
  icon: IconType
  color: {
    light: string
    dark: string
    group: ChartColorGroupName
  }
  name: string
  description: string
  canAddStatuses: boolean
}

const statusColours = {
  blue: {
    light: 'var(--color-blue-50)',
    dark: 'var(--color-blue-500)',
    group: 'blue',
  },
  greenPale: {
    light: 'var(--color-green-50)',
    dark: 'var(--color-green-500)',
    group: 'green',
  },
  green: {
    light: 'var(--color-green-50)',
    dark: 'var(--color-green-500)',
    group: 'green',
  },
  orange: {
    light: 'var(--color-amber-50)',
    dark: 'var(--color-amber-500)',
    group: 'orange',
  },
  red: {
    light: 'var(--color-red-50)',
    dark: 'var(--color-red-500)',
    group: 'red',
  },
  gray: {
    light: 'var(--color-gray-50)',
    dark: 'var(--color-gray-500)',
    group: 'gray',
  },
} as const satisfies Record<string, ReferralStageInfo['color']>

class ReferralStageService {
  getBaseSortOrder(stage: ReferralStage): number {
    switch (stage) {
      case 'referred':
        return 1000
      case 'engaging':
        return 2000
      case 'converting':
        return 3000
      case 'closed_won_recurring':
        return 4000
      case 'closed_won_one_off':
        return 5000
      case 'stalled':
        return 6000
      case 'closed_lost':
        return 7000
      case 'not_sure':
        return 8000
    }
  }

  getReferralStatusInfo(
    status: Pick<ReferralStatusEntity, 'code' | 'stage'> | null,
  ): ReferralStageInfo {
    const info = this.getReferralStageInfo(status?.stage || 'not_sure')
    if (
      status?.code === 'awaiting_client_consent' ||
      status?.code === 'awaiting_client_schedule_meeting'
    ) {
      info.icon = LuClock
    }

    return info
  }

  getReferralStageInfo(stage: ReferralStage) {
    const styles: Record<ReferralStage, ReferralStageInfo> = {
      referred: {
        code: 'referred',
        icon: LuClock,
        color: statusColours.blue,
        name: REFERRAL_STAGE_LABEL.referred,
        description: 'The prospect has been introduced.',
        canAddStatuses: true,
      },

      engaging: {
        code: 'engaging',
        icon: LuClock,
        color: statusColours.blue,
        name: REFERRAL_STAGE_LABEL.engaging,
        description:
          'Active discussions and relationship-building with the client are underway.',
        canAddStatuses: true,
      },

      converting: {
        code: 'converting',
        icon: LuUsers,
        color: statusColours.greenPale,
        name: REFERRAL_STAGE_LABEL.converting,
        description:
          'The client is close to committing, with negotiations or final decision-making in progress.',
        canAddStatuses: true,
      },

      closed_won_recurring: {
        code: 'closed_won_recurring',
        icon: LuTrophy,
        color: statusColours.green,
        name: REFERRAL_STAGE_LABEL.closed_won_recurring,
        description:
          'The client has committed and represents ongoing or repeat business.',
        canAddStatuses: true,
      },

      closed_won_one_off: {
        code: 'closed_won_one_off',
        icon: LuTrophy,
        color: statusColours.green,
        name: REFERRAL_STAGE_LABEL.closed_won_one_off,
        description:
          'The client has committed to a single, non-recurring transaction.',
        canAddStatuses: true,
      },

      stalled: {
        code: 'stalled',
        icon: LuPause,
        color: statusColours.orange,
        name: REFERRAL_STAGE_LABEL.stalled,
        description:
          'Progress has slowed or paused with no recent movement from the client.',
        canAddStatuses: true,
      },

      closed_lost: {
        code: 'closed_lost',
        icon: LuOctagonMinus,
        color: statusColours.red,
        name: REFERRAL_STAGE_LABEL.closed_lost,
        description:
          'The opportunity has ended without success or the client has chosen not to proceed.',
        canAddStatuses: true,
      },

      not_sure: {
        code: 'not_sure',
        icon: LuCircleHelp,
        color: statusColours.gray,
        name: REFERRAL_STAGE_LABEL.not_sure,
        description:
          "The client's status or next steps are currently unclear or undefined.",
        canAddStatuses: false,
      },
    }

    if (!styles[stage]) {
      throw new Error(
        `Failed to determine styles for the referral stage: ${stage}`,
      )
    }

    return styles[stage]
  }
}

const referralStageService = new ReferralStageService()

export default referralStageService
